import { useState, useCallback, useRef, useEffect } from "react";
import "@/App.css";
import { ReactLenis } from "lenis/react";
import { Toaster } from "@/components/ui/sonner";

import { CONTENT } from "@/content";
import GrainOverlay from "@/components/GrainOverlay";
import RevealGate from "@/components/RevealGate";
import Celebration from "@/components/Celebration";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Gallery from "@/components/Gallery";
import Timeline from "@/components/Timeline";
import VideoSection from "@/components/VideoSection";
import FinalMessage from "@/components/FinalMessage";
import AudioPlayer from "@/components/AudioPlayer";

// Music volume envelope: gentle fade-in and fade-out near the loop point.
const FADE_SECONDS = 3.5;
const BASE_VOLUME = 0.85;

function App() {
  const [opened, setOpened] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  // Drive the volume from playback time so the track fades out as it nears the
  // end and fades back in as it loops — a seamless, gentle loop.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const onTime = () => {
      const d = audio.duration;
      if (!d || Number.isNaN(d)) return;
      const t = audio.currentTime;
      const remaining = d - t;
      const env = Math.max(
        0,
        Math.min(1, t / FADE_SECONDS, remaining / FADE_SECONDS),
      );
      audio.volume = BASE_VOLUME * env;
    };
    audio.addEventListener("timeupdate", onTime);
    return () => audio.removeEventListener("timeupdate", onTime);
  }, []);

  const handleOpen = useCallback(() => {
    setOpened(true);
    setCelebrate(true);
    // play within the tap gesture so autoplay-with-sound is allowed
    const audio = audioRef.current;
    if (audio) {
      audio.volume = 0;
      audio
        .play()
        .then(() => setPlaying(true))
        .catch(() => setPlaying(false));
    }
    setTimeout(() => setCelebrate(false), 7000);
  }, []);

  const toggleMusic = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      audio.pause();
      setPlaying(false);
    }
  }, []);

  const handleUnlock = useCallback(() => {
    setCelebrate(true);
    setTimeout(() => setCelebrate(false), 8000);
  }, []);

  const replay = useCallback(() => {
    setCelebrate(true);
    setTimeout(() => setCelebrate(false), 8000);
  }, []);

  return (
    <ReactLenis root options={{ lerp: 0.08, smoothWheel: true }}>
      <div className="relative min-h-screen" data-testid="app-root">
        <GrainOverlay />
        <audio
          ref={audioRef}
          src={CONTENT.music.url}
          loop
          preload="auto"
          onError={() => {}}
        />

        <Celebration active={celebrate} />

        {!opened && <RevealGate onOpen={handleOpen} onUnlock={handleUnlock} />}

        {opened && (
          <main data-testid="site-main" className="site-enter">
            <Hero />
            <Marquee />
            <Gallery />
            <Timeline />
            <VideoSection />
            <FinalMessage onCelebrate={replay} />
            <AudioPlayer playing={playing} onToggle={toggleMusic} />
          </main>
        )}

        <Toaster
          position="top-center"
          theme="dark"
          toastOptions={{
            style: {
              background: "rgba(26,12,17,0.9)",
              border: "1px solid rgba(232,180,184,0.2)",
              color: "#faf7f2",
            },
          }}
        />
      </div>
    </ReactLenis>
  );
}

export default App;
